// #ux-redesign-start: StudyAnalytes — resultados agrupados por clasificador
//
// Comportamiento:
//   - Desktop (lg+): master-detail — clasificadores como tab lateral a la
//     izquierda, la tabla del clasificador activo a la derecha. Sin acordeón.
//     El primer clasificador queda seleccionado por defecto.
//   - Mobile / tablet: acordeón con click-toggle (un clasificador abierto).
//     "Expandir todo" / "Colapsar todo" override explícito.
//   - Búsqueda activa: filtra los analitos; en acordeón abre todas las
//     secciones con matches. Highlight inline del substring.
//   - Agrupación: NO seleccionable por el paciente — siempre el Grupo default
//     (`is_default_for_doctor`). A futuro la indicará el médico.
//
// Labels via `useTranslate('analytes-groups')`. Ver .ia/32-analytes-catalog.md.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-translate';
import { DataTable, Badge, Input, Spinner } from 'components/ds';
import useResponsive from 'hooks/useResponsive';

import AnalytesGroupsService, {
  DEFAULT_GROUP_KEY,
} from 'services/AnalytesGroupsService';

const ABNORMAL_FLAGS = ['H', 'L', 'HH', 'LL', 'A', '*'];

const isAbnormal = (flag) => {
  if (!flag) return false;
  const trimmed = String(flag).trim().toUpperCase();
  return ABNORMAL_FLAGS.some((f) => trimmed === f);
};

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckSmallIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const escapeHtml = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Formatea una fila de analito para clipboard. Devuelve { plain, html }
// listo para pegar en el WYSIWYG (Quill) — Quill prioriza text/html y cae a
// text/plain si no está disponible.
const formatRowForClipboard = (row, abnormal) => {
  const flag = abnormal ? ` [${String(row.flag).trim().toUpperCase()}]` : '';
  const valueChunk = `${row.value || ''} ${row.units || ''}${flag}`.trim();
  const refChunk = row.range ? ` (Ref: ${row.range})` : '';

  const plain = `${row.identifier}: ${valueChunk}${refChunk}`.replace(/\s+/g, ' ').trim();

  const valueHtml = abnormal
    ? `<strong>${escapeHtml(valueChunk)}</strong>`
    : escapeHtml(valueChunk);
  const refHtml = row.range ? ` <em>${escapeHtml(`(Ref: ${row.range})`)}</em>` : '';
  const html = `<strong>${escapeHtml(row.identifier)}:</strong> ${valueHtml}${refHtml}`;
  return { plain, html };
};

const copyToClipboard = async ({ plain, html }) => {
  try {
    if (navigator.clipboard && navigator.clipboard.write && typeof window.ClipboardItem === 'function') {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return true;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(plain);
      return true;
    }
  } catch (err) {
    // fallthrough
  }
  return false;
};

const CopyRowButton = ({ row, abnormal, labels }) => {
  const [copied, setCopied] = React.useState(false);
  const handleClick = async (e) => {
    e.stopPropagation();
    const ok = await copyToClipboard(formatRowForClipboard(row, abnormal));
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <button
      type="button"
      className={`ds-lab-detail__analyte-copy ${copied ? 'is-copied' : ''}`}
      onClick={handleClick}
      aria-label={labels.copyRow}
      title={copied ? labels.copied : labels.copyRow}
    >
      {copied ? <CheckSmallIcon /> : <CopyIcon />}
    </button>
  );
};

const highlightMatch = (text, query) => {
  if (!query || text === null || text === undefined) return text;
  const t = String(text);
  if (!t) return t;
  const q = query.trim();
  if (!q) return t;
  const qLen = q.length;
  const lowerT = t.toLowerCase();
  const lowerQ = q.toLowerCase();

  const parts = [];
  let cursor = 0;
  let next = lowerT.indexOf(lowerQ, cursor);
  let key = 0;
  while (next !== -1) {
    if (next > cursor) parts.push(t.slice(cursor, next));
    parts.push(
      <mark key={`m-${key++}`} className="ds-lab-detail__analyte-highlight">
        {t.slice(next, next + qLen)}
      </mark>
    );
    cursor = next + qLen;
    next = lowerT.indexOf(lowerQ, cursor);
  }
  if (cursor < t.length) parts.push(t.slice(cursor));
  return parts.length > 1 ? <>{parts}</> : t;
};

const StudyAnalytes = ({ study }) => {
  const t = useTranslate('laboratory-detail');
  const tGroups = useTranslate('analytes-groups');
  const [query, setQuery] = useState('');
  const queryTrim = query.trim();
  const queryActive = queryTrim.length > 0;

  const allRows = useMemo(() => {
    if (!Array.isArray(study?.medicalValues)) return [];
    return study.medicalValues.map((p, idx) => ({
      id: idx,
      identifier: (p?.['Observation Identifier'] || '').replace(/^([\S]{1,})\s/, ''),
      rawIdentifier: p?.['Observation Identifier'] || '',
      value: p?.['Observation Value'] || '',
      units: p?.['Units'] || '',
      range: p?.['References Range'] || '',
      flag: p?.['Abnormal Flags'] || '',
      comment: Array.isArray(p?.['Comment']) ? p['Comment'] : [],
    }));
  }, [study]);

  const { isDesktop } = useResponsive();

  // ── Grupo activo ─────────────────────────────────────────────────────────
  // Siempre el default. La agrupación no es seleccionable por el paciente —
  // a futuro la indicará el médico. Se busca el `is_default_for_doctor`.
  const [activeGroupKey, setActiveGroupKey] = useState(DEFAULT_GROUP_KEY);

  useEffect(() => {
    let cancelled = false;
    AnalytesGroupsService.list()
      .then((res) => {
        if (cancelled) return;
        const list = (res && (res.data || res)) || [];
        if (!Array.isArray(list)) return;
        const defaultDoc = list.find((g) => g.is_default_for_doctor);
        if (defaultDoc) setActiveGroupKey(defaultDoc.key);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Resolve del Grupo activo + classifications ───────────────────────────
  const [group, setGroup] = useState(null);
  const [classifications, setClassifications] = useState({});
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!allRows.length) {
      setGroup(null);
      setClassifications({});
      return;
    }
    let cancelled = false;
    setResolving(true);
    const identifiers = [...new Set(allRows.map((r) => r.rawIdentifier).filter(Boolean))];
    AnalytesGroupsService.resolve(identifiers, activeGroupKey)
      .then((res) => {
        if (cancelled) return;
        const data = res && (res.data || res);
        setGroup(data.group || null);
        setClassifications(data.classifications || {});
      })
      .catch(() => {
        if (!cancelled) { setGroup(null); setClassifications({}); }
      })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [allRows, activeGroupKey]);

  // ── Filtro de búsqueda + secciones ───────────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = queryTrim.toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) =>
      [r.identifier, r.value, r.units, r.range]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [allRows, queryTrim]);

  const sections = useMemo(() => {
    if (!group || !group.classifiers) {
      return filteredRows.length
        ? [{ key: '_all', label: null, order: 0, rows: filteredRows }]
        : [];
    }
    const fallback = group.classifiers.find((c) => c.is_fallback) || { key: 'general' };
    const buckets = {};
    for (const c of group.classifiers) buckets[c.key] = [];
    for (const row of filteredRows) {
      const classifierKey = classifications[row.rawIdentifier] || fallback.key;
      if (!buckets[classifierKey]) buckets[classifierKey] = [];
      buckets[classifierKey].push(row);
    }
    return group.classifiers
      .map((c) => ({
        key: c.key,
        label: tGroups(`${c.key}.name`),
        order: c.order,
        rows: buckets[c.key] || [],
      }))
      .filter((s) => s.rows.length > 0)
      .sort((a, b) => a.order - b.order);
  }, [filteredRows, group, classifications, tGroups]);

  // ── Accordion state — Set<string> de open keys ────────────────────────────
  const [openKeys, setOpenKeys] = useState(() => new Set());

  // Default: primer sección abierta. Re-engancha si el primer key cambia
  // (por cambio de Grupo, de estudio, etc.).
  useEffect(() => {
    if (!sections.length) {
      if (openKeys.size > 0) setOpenKeys(new Set());
      return;
    }
    const validKeys = new Set(sections.map((s) => s.key));
    const filtered = new Set();
    for (const k of openKeys) if (validKeys.has(k)) filtered.add(k);
    if (filtered.size === 0) {
      setOpenKeys(new Set([sections[0].key]));
    } else if (filtered.size !== openKeys.size) {
      setOpenKeys(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  // ── Master-detail (desktop): clasificador activo del tab lateral ──────────
  // Por defecto SIEMPRE el primero. Sólo se conserva otra selección si la
  // eligió el usuario — así un render transitorio (classifications aún vacías
  // → todo cae a "general") no puede dejar fijado el último tab. React 16 no
  // batchea los setState del .then de resolve(), por eso ese render existe.
  const userPickedSectionRef = useRef(false);
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  useEffect(() => {
    if (!sections.length) { setSelectedSectionKey(null); return; }
    setSelectedSectionKey((prev) => {
      const stillValid = prev && sections.some((s) => s.key === prev);
      if (userPickedSectionRef.current && stillValid) return prev;
      return sections[0].key;
    });
  }, [sections]);

  // Derivado: durante búsqueda, TODAS las secciones con matches están abiertas.
  // Sino: respeta openKeys.
  const effectiveOpenKeys = useMemo(() => {
    if (queryActive) return new Set(sections.map((s) => s.key));
    return openKeys;
  }, [queryActive, sections, openKeys]);

  const allOpen = sections.length > 0 && effectiveOpenKeys.size === sections.length;

  const toggleSection = (key) => {
    if (queryActive) return;
    setOpenKeys((prev) => {
      // Click en el único abierto → cierra todo. Sino → single-expand a esa key.
      if (prev.size === 1 && prev.has(key)) return new Set();
      return new Set([key]);
    });
  };

  const toggleAll = () => {
    if (queryActive) return;
    setOpenKeys(allOpen ? new Set() : new Set(sections.map((s) => s.key)));
  };

  const useMasterDetail = isDesktop && sections.length > 1;
  const currentSection =
    sections.find((s) => s.key === selectedSectionKey) || sections[0] || null;

  const columns = useMemo(() => [
    {
      Header: t('observation-identifier'),
      accessor: 'identifier',
      Cell: ({ row }) => {
        const abnormal = isAbnormal(row.original.flag);
        return (
          <span className={abnormal ? 'ds-lab-detail__analyte-name ds-lab-detail__analyte-name--abnormal' : 'ds-lab-detail__analyte-name'}>
            {highlightMatch(row.original.identifier, queryTrim)}
          </span>
        );
      },
    },
    {
      Header: t('observation-value'),
      accessor: 'value',
      disableSortBy: true,
      Cell: ({ row }) => {
        const abnormal = isAbnormal(row.original.flag);
        return (
          <span className={abnormal ? 'ds-lab-detail__analyte-value ds-lab-detail__analyte-value--abnormal' : 'ds-lab-detail__analyte-value'}>
            {highlightMatch(row.original.value, queryTrim)}
            {abnormal && (
              <Badge variant="danger" size="sm" className="ds-lab-detail__analyte-flag">
                {String(row.original.flag).trim().toUpperCase()}
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      Header: t('units'),
      accessor: 'units',
      disableSortBy: true,
      Cell: ({ value }) => highlightMatch(value, queryTrim),
    },
    {
      Header: t('references-range'),
      accessor: 'range',
      disableSortBy: true,
      Cell: ({ value }) => highlightMatch(value, queryTrim),
    },
    {
      Header: t('comment'),
      accessor: 'comment',
      disableSortBy: true,
      Cell: ({ value }) => (
        <div className="ds-lab-detail__analyte-comment">
          {(value || []).map((line, i) => <p key={i}>{line}</p>)}
        </div>
      ),
    },
    {
      Header: '',
      accessor: '_copy',
      disableSortBy: true,
      Cell: ({ row }) => (
        <CopyRowButton
          row={row.original}
          abnormal={isAbnormal(row.original.flag)}
          labels={{ copyRow: t('copy-row'), copied: t('copied') }}
        />
      ),
    },
  ], [t, queryTrim]);

  return (
    <div className="ds-lab-detail__analytes">
      <div className="ds-lab-detail__analytes-toolbar">
        <Input
          name="analytes-search"
          placeholder={t('search-analytes')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          iconLeft={<SearchIcon />}
          aria-label={t('search-analytes')}
        />
        {/* Expandir/colapsar — solo en modo acordeón (mobile / tablet). */}
        {!useMasterDetail && sections.length > 1 && (
          <button
            type="button"
            className="ds-lab-detail__analytes-expand-toggle"
            onClick={toggleAll}
            disabled={queryActive}
          >
            {allOpen ? t('collapse-all') : t('expand-all')}
          </button>
        )}
        {resolving && (
          <span className="ds-lab-detail__analytes-resolving" aria-label={t('loading-study')}>
            <Spinner size="sm" />
          </span>
        )}
      </div>

      {sections.length === 0 ? (
        <DataTable
          columns={columns}
          data={[]}
          empty={query ? t('no-search-results') : t('no-results')}
          manualSortBy={false}
          manualPagination={false}
        />
      ) : useMasterDetail ? (
        /* ── Desktop: master-detail (tab lateral de clasificadores) ──────── */
        <div className="ds-lab-detail__analytes-split">
          <nav
            className="ds-lab-detail__analytes-nav"
            aria-label={t('classifiers')}
          >
            {sections.map((section) => {
              const active = section.key === currentSection?.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  className={`ds-lab-detail__analytes-nav-item ${active ? 'is-active' : ''}`}
                  onClick={() => {
                    userPickedSectionRef.current = true;
                    setSelectedSectionKey(section.key);
                  }}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className="ds-lab-detail__analytes-nav-label">
                    {section.label}
                  </span>
                  <span className="ds-lab-detail__analytes-nav-count">
                    {section.rows.length}
                  </span>
                </button>
              );
            })}
          </nav>
          <div className="ds-lab-detail__analytes-panel">
            {currentSection && (
              <DataTable
                columns={columns}
                data={currentSection.rows}
                empty={query ? t('no-search-results') : t('no-results')}
                manualSortBy={false}
                manualPagination={false}
                rowClassName={(rowData) => (rowData && isAbnormal(rowData.flag) ? 'ds-lab-detail__analyte-row--abnormal' : '')}
              />
            )}
          </div>
        </div>
      ) : isDesktop ? (
        /* ── Desktop con un solo clasificador: tabla directa, sin acordeón ─ */
        <DataTable
          columns={columns}
          data={sections[0] ? sections[0].rows : []}
          empty={query ? t('no-search-results') : t('no-results')}
          manualSortBy={false}
          manualPagination={false}
          rowClassName={(rowData) => (rowData && isAbnormal(rowData.flag) ? 'ds-lab-detail__analyte-row--abnormal' : '')}
        />
      ) : (
        /* ── Mobile / tablet: acordeón ──────────────────────────────────── */
        sections.map((section) => {
          const isOpen = effectiveOpenKeys.has(section.key);
          return (
            <section
              className={`ds-lab-detail__analytes-section ${isOpen ? 'is-open' : ''}`}
              key={section.key}
            >
              <button
                type="button"
                className="ds-lab-detail__analytes-section-head"
                aria-expanded={isOpen}
                aria-controls={`analytes-section-body-${section.key}`}
                onClick={() => toggleSection(section.key)}
                disabled={queryActive}
                testId={`analytes-section-${section.key}`}
              >
                <h3 className="ds-lab-detail__analytes-section-title">
                  {section.label}
                </h3>
                <span className="ds-lab-detail__analytes-section-chevron" aria-hidden="true">
                  <ChevronIcon open={isOpen} />
                </span>
              </button>

              {isOpen && (
                <div
                  id={`analytes-section-body-${section.key}`}
                  className="ds-lab-detail__analytes-section-body"
                  role="region"
                >
                  <DataTable
                    columns={columns}
                    data={section.rows}
                    empty={query ? t('no-search-results') : t('no-results')}
                    manualSortBy={false}
                    manualPagination={false}
                    rowClassName={(rowData) => (rowData && isAbnormal(rowData.flag) ? 'ds-lab-detail__analyte-row--abnormal' : '')}
                  />
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default StudyAnalytes;
// #ux-redesign-end
